import { useSyncExternalStore } from "react";
import { shouldShowDevTools, shouldShowMaskToggle } from "../util/common";

// Custom event fired after we monkey-patch history.pushState/replaceState so
// that in-app (react-router) and programmatic navigations also notify
// subscribers, not just back/forward (which fire the native popstate event).
const LOCATION_CHANGE_EVENT = "cip:locationchange";

let historyPatched = false;

const patchHistory = (): void => {
  if (historyPatched || typeof window === "undefined") return;
  historyPatched = true;

  const dispatch = () => window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));

  (["pushState", "replaceState"] as const).forEach((method) => {
    const original = window.history[method];
    window.history[method] = function (
      this: History,
      ...args: Parameters<History["pushState"]>
    ) {
      const result = original.apply(this, args);
      dispatch();
      return result;
    };
  });
};

const subscribe = (callback: () => void): (() => void) => {
  patchHistory();
  window.addEventListener("popstate", callback);
  window.addEventListener("hashchange", callback);
  window.addEventListener(LOCATION_CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener("popstate", callback);
    window.removeEventListener("hashchange", callback);
    window.removeEventListener(LOCATION_CHANGE_EVENT, callback);
  };
};

/**
 * Reactive version of `shouldShowMaskToggle`. Re-evaluates whenever the URL
 * changes (including `?showtools=true|false` overrides) so the UI updates live
 * without a page refresh.
 */
export const useShouldShowMaskToggle = (): boolean =>
  useSyncExternalStore(subscribe, shouldShowMaskToggle, shouldShowMaskToggle);

/**
 * Reactive version of `shouldShowDevTools`. Re-evaluates whenever the URL
 * changes (including `?showtools=true|false` overrides) so the UI updates live
 * without a page refresh.
 */
export const useShouldShowDevTools = (): boolean =>
  useSyncExternalStore(subscribe, shouldShowDevTools, shouldShowDevTools);
